import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  Paper,
  Avatar,
  TableContainer,
  Autocomplete,
} from "@mui/material";
import { Grid, Stack } from "@mui/system";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SaveIcon from "@mui/icons-material/Save";
import CheckIcon from "@mui/icons-material/Check";
import { useState } from "react";
import { setSelectedRows } from "../../../../../../features/businessInsightsSlice";
import { useDispatch, useSelector } from "react-redux";
import DataStatusAlert from "../../../../Health/DataAccuracy/UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import { useUpdateBusinessInsightsStatus } from "../../../../../../customHooks/useUpdateBusinessInsightsStatus";
import dayjs from "dayjs";
import SimpleBackdrop from "../../../../../BackDrop/BackDrop";

interface Props {
  open: boolean;
  handleClose: () => void;
}

const SnoozeInsightsPopup = ({ open, handleClose }: Props) => {
  const dispatch = useDispatch();
  const selectedRows = useSelector(
    (state: any) => state?.businessInsights?.selectedRows
  );
  const [comment, setComment] = useState<string>("");
  const [isshowMsg, setIsShowMsg] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selected, setSelected] = useState<any[]>(selectedRows);
  const [status, setStatus] = useState(null) as any;

  //-- Check if all rows are selected
  const isAllSelected = selected.length === selectedRows.length;

  const { loading, updateBusinessInsightsStatus } =
    useUpdateBusinessInsightsStatus("snoozed"); // Custom hook for updating status(Resolved)

  //-- Close the Snooze modal and clear the selected rows(unchecked checkboxes)
  const handleCloseSnoozeModal = () => {
    handleClose(); // Close modal if success
    dispatch(setSelectedRows([])); // Clear selected rows
  };

  //-- Handle individual row selection
  const handleSelectRow = (row: any) => {
    setSelected((prev) =>
      prev.some((r) => r === row)
        ? prev.filter((r) => r !== row)
        : [...prev, row]
    );
  };

  //-- Handle "Select All" checkbox
  const handleSelectAll = () => {
    setSelected(isAllSelected ? [] : [...selectedRows]);
  };

  //-- Check if the button is disabled or not
  const isButtonDisabled = () => {
    if (selected.length === 0 || !selectedDate || isshowMsg) return true;
    if (selected.length > 1 && (!comment || !selectedDate)) return true;
    if ((selected.length > 1 && !comment)) return true;
    return false;
  };

  const checkIsLessDueDate = (snoozeDate: any) => {
    setIsShowMsg(false);
    //-- If due_date <= snooze_end date then show error message according to docs
    const invalidTasks = selected.some(
      (row) =>
        dayjs(row?.due_date).format("YYYY-MM-DD") <=
        dayjs(snoozeDate).format("YYYY-MM-DD")
    );

    if (invalidTasks) {
      setIsShowMsg(true);
      //return;
    }
    if (!invalidTasks) {
      setIsShowMsg(false);
    }
  };

  //-- Request body for updating task status Snooze
  const snoozeBodyActionItem = {
    tasks: selected.map((task) => ({
      shared_uid: task.shared_uid,
      current_status: status,
      snooze_end: selectedDate,
    })),
  };

  const StatusList = [
    { label: "To Do", value: "to_do" },
    { label: "In Progress", value: "blocked" },
    { label: "Snooze", value: "snoozed" },
    { label: "Done", value: "done" },
  ];

  //-- Clicking the Snooze button to update the status
  const handleActions = async () => {
    if (!selectedDate || selected.length === 0) return;
    //-- this function for update status(MAKE API call for update status)
    updateBusinessInsightsStatus(
      snoozeBodyActionItem,
      comment,
      handleCloseSnoozeModal,
      null, // assigneeName - not used for snooze
      status // pass the selected status
    );
    setIsShowMsg(false); // Clear error message
  };

  return (
    <>
      {loading ? (
        <SimpleBackdrop />
      ) : (
        <Dialog open={open} maxWidth="lg" fullWidth>
          <DialogTitle sx={{}}>Change Insight Status</DialogTitle>
          <DialogContent>
            <Paper
              sx={{
                width: "100%",
                overflowY: "auto",
                height: "auto",
                maxHeight: "225px",
                scrollbarWidth: "thin",
                marginBottom: "20px",
              }}
            >
              <TableContainer sx={{ marginBottom: "16px" }}>
              <Table size="small" aria-label="a dense table">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ padding: "4px" }}>
                      <Checkbox
                        size="small"
                        color="secondary"
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: "4px" }}>Task Name</TableCell>
                    <TableCell sx={{ padding: "4px" }}>Assignee</TableCell>
                    <TableCell sx={{ padding: "4px" }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedRows?.map((row: any, index: number) => (
                    <TableRow
                      key={index}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell padding="checkbox" sx={{ padding: "4px" }}>
                        <Checkbox
                          size="small"
                          color="secondary"
                          checked={selected.some((r) => r === row)}
                          onChange={() => handleSelectRow(row)}
                        />
                      </TableCell>
                      <TableCell sx={{ padding: "4px" }}>
                        {row?.data["query_output:sec_title"]?.value || "N/A"}
                      </TableCell>
                      <TableCell sx={{ padding: "4px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Avatar sx={{ width: 22, height: 22, fontSize: 12 }}>
                            {row?.assignee_fullname.charAt(0).toUpperCase()}
                          </Avatar>
                          {row?.assignee_fullname}
                        </div>
                      </TableCell>
                      <TableCell sx={{ padding: "4px" }}>
                        {row?.current_status && (
                          <DataStatusAlert status={row?.current_status} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </TableContainer>
            </Paper>
            <Grid container spacing={2}>
              <Grid size={6}>
                 <Autocomplete
                  disablePortal={false}
                  options={StatusList}
                  value={
                    StatusList.find((option) => option.label === status) || null
                  }
                  getOptionLabel={(option: any) => option.label}
                  onChange={(_, newValue) =>
                    setStatus(newValue ? newValue.label : null)
                  }
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      sx={{
                        paddingTop: 1,
                        "& .MuiInputLabel-root": {
                          top: 8,
                        },
                      }}
                      {...params}
                      label="Status"
                      size="small"
                      required
                      helperText={"This field is mandatory"}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      placeholder="Choose Status from the list"
                      color="secondary"
                    />
                  )}
                />
              </Grid>
              <Grid size={6} sx={{ paddingTop: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    className="brandDataDatePicker"
                    slotProps={{
                      textField: {
                        size: "small",
                        color: "secondary",
                        required: true,
                        fullWidth: true,
                        placeholder: "Pick a date",
                        InputLabelProps: { shrink: true },
                        inputProps: {
                          readOnly: true,
                          onKeyDown: (e) => {
                            // Block ALL keys including numbers and delete
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onKeyPress: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onKeyUp: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onInput: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onBeforeInput: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onPaste: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onCut: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onChange: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onFocus: (e) => {
                            e.target.blur(); // Remove focus immediately
                          },
                          style: {
                            pointerEvents: "none",
                            caretColor: "transparent",
                            userSelect: "none",
                            WebkitUserSelect: "none",
                            MozUserSelect: "none",
                            cursor: "default",
                          },
                        },
                        sx: {
                          "& .MuiInputBase-input": {
                            pointerEvents: "none !important",
                            caretColor: "transparent !important",
                            userSelect: "none !important",
                            WebkitUserSelect: "none !important",
                            MozUserSelect: "none !important",
                            "&:focus": {
                              outline: "none !important",
                              boxShadow: "none !important",
                            },
                          },
                          "& .MuiIconButton-root": {
                            pointerEvents: "auto !important",
                            cursor: "pointer !important",
                          },
                        },
                      },
                    }}
                    format="DD/MM/YYYY"
                    maxDate={dayjs("2030-12-31")}
                    //minDate={dayjs('' + new Date().getFullYear() + '-01-01')}
                    minDate={dayjs()} // Prevent selection of past dates
                    disablePast // Disables all past dates automatically
                    label="Status Action Date"
                    onChange={(date: any) => {
                      if (date) {
                        setSelectedDate(
                          dayjs(date).format("YYYY-MM-DD HH:mm:ss")
                        );
                        checkIsLessDueDate(dayjs(date).format("YYYY-MM-DD")); // check due_date <= snooze_end date
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Reason"
                  size="small"
                  color="secondary"
                  required={selected?.length > 1}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Why item is snoozed"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ padding: "20px" }}>
            {isshowMsg && (
              <Typography color="error">
                One or more actions can’t be snoozed after their Due date.
                Please adjust Due date or Snooze date accordingly.
              </Typography>
            )}
            <Button
              onClick={() => {
                handleClose();
                dispatch(setSelectedRows([]));
              }}
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              data-tracker="120"
              variant="contained"
              color="secondary"
              loading={loading}
              loadingPosition="end"
              endIcon={loading && <SaveIcon />}
              disabled={isButtonDisabled()}
              onClick={() => handleActions()}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                Change Status
                <CheckIcon fontSize="small" />
              </Stack>
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default SnoozeInsightsPopup;
